import { collection, doc, setDoc, getDoc, updateDoc, deleteDoc, query, where, getDocs, runTransaction } from 'firebase/firestore';
import { db } from '../config/firebase';
import { encrypt, decrypt } from '../utils/encryption';

/**
 * 패스워드 공유 서비스
 * Firebase shared_keys 컬렉션을 통해 안전한 패스워드 공유 기능 제공
 */

/**
 * 공유키 생성
 * @param {string} userId - 공유하는 사용자 ID
 * @param {Object} passwordData - 공유할 패스워드 데이터
 * @param {Object} options - 공유 옵션 (만료시간, 사용횟수, 메모 등)
 * @returns {Promise<string>} 생성된 공유키
 */
export async function createShareKey(userId, passwordData, options = {}) {
  try {
    // 🔍 디버깅: passwordData 구조 확인
    console.log('공유키 생성 - passwordData:', passwordData);
    console.log('공유키 생성 - passwordData.title:', passwordData.title);
    console.log('공유키 생성 - options.shareNote:', options.shareNote); // 🆕 메모 디버깅
    
    // 암호학적으로 안전한 공유키 생성
    const shareKey = generateShareKey();
    
    // URL에 사용할 액세스 토큰 생성 (실제 공유키와 분리)
    const accessToken = generateAccessToken();
    
    // 만료 시간 계산 (기본: 24시간)
    const expirationHours = options.expirationHours || 24;
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + expirationHours);
    
    // 최대 사용 횟수 (기본: 5회)
    const maxUses = options.maxUses || 5;
    
    // 패스워드 데이터 암호화 (공유키를 암호화 키로 사용)
    const encryptedData = await encrypt(JSON.stringify(passwordData), shareKey);
    
    // 공유키 메타데이터
    const shareMetadata = {
      shareKey,
      accessToken, // URL에 사용될 토큰
      sharedBy: userId,
      sharedByName: options.userName || '알 수 없음', // 사용자 이름 추가
      sharedByEmail: options.userEmail || '', // 사용자 이메일 추가
      encryptedData,
      passwordTitle: passwordData.title || '제목 없음', // 패스워드 제목 저장 추가
      passwordWebsite: passwordData.website || '', // 웹사이트도 함께 저장
      shareNote: options.shareNote || '', // 🆕 공유 메모 저장 (공유 링크에는 포함되지 않음)
      createdAt: new Date(),
      expiresAt,
      maxUses,
      currentUses: 0,
      isActive: true,
      accessLog: [],
      // 보안 메타데이터
      securityMetadata: {
        keyVersion: '2025.07',
        algorithm: 'AES-256-GCM',
        keyDerivation: 'PBKDF2'
      }
    };
    
    // 🔍 디버깅: 저장할 메타데이터 확인
    if (import.meta.env.DEV) {
      console.log('공유키 생성 중...');
      console.log('Share Key:', shareKey);
      console.log('Access Token:', accessToken);
    }
    
    // Firestore에 저장 - accessToken을 문서 ID로 사용
    const shareRef = doc(db, 'shared_keys', accessToken);
    await setDoc(shareRef, shareMetadata);
    
    if (import.meta.env.DEV) {
      console.log('공유키 생성 완료');
    }
    return accessToken; // URL에 사용할 액세스 토큰 반환
    
  } catch (error) {
    console.error('공유키 생성 실패:', error);
    throw new Error('공유키 생성에 실패했습니다.');
  }
}

/**
 * 공유된 패스워드 조회 (메모는 포함하지 않음)
 * @param {string} accessToken - 액세스 토큰 (URL에서 가져온)
 * @param {string} accessorId - 접근자 ID (optional)
 * @returns {Promise<Object>} 복호화된 패스워드 데이터
 */
export async function getSharedPassword(accessToken, accessorId = null) {
  try {
    const shareRef = doc(db, 'shared_keys', accessToken);
    const shareDoc = await getDoc(shareRef);
    
    if (!shareDoc.exists()) {
      throw new Error('유효하지 않은 공유 링크입니다.');
    }
    
    const shareData = shareDoc.data();
    
    // 활성 상태 확인
    if (!shareData.isActive) {
      throw new Error('비활성화된 공유 링크입니다.');
    }
    
    // 만료 시간 확인
    if (new Date() > shareData.expiresAt.toDate()) {
      throw new Error('만료된 공유 링크입니다.');
    }
    
    // 사용 횟수 확인
    if (shareData.currentUses >= shareData.maxUses) {
      throw new Error('사용 한도를 초과한 공유 링크입니다.');
    }
    
    // 패스워드 데이터 복호화
    // 새 방식: shareData.shareKey 사용
    // 구 방식: accessToken이 곧 shareKey (하위 호환성)
    const decryptKey = shareData.shareKey || accessToken;
    const decryptedData = await decrypt(shareData.encryptedData, decryptKey);
    const passwordData = JSON.parse(decryptedData);
    
    // 메타데이터와 함께 반환 (🚨 shareNote는 포함하지 않음)
    return {
      passwordData,
      shareInfo: {
        sharedBy: shareData.sharedBy,
        createdAt: shareData.createdAt,
        expiresAt: shareData.expiresAt,
        remainingUses: shareData.maxUses - shareData.currentUses, // 현재 상태 기준
        securityMetadata: shareData.securityMetadata
      }
    };
    
  } catch (error) {
    console.error('공유 패스워드 조회 실패:', error);
    throw error;
  }
}

/**
 * 공유 링크 사용 시 회수 차감 및 접근 로그 기록
 * @param {string} accessToken - 액세스 토큰
 * @param {string} accessorId - 접근자 ID (optional)
 * @param {string} accessorInfo - 접근자 정보 (IP, User-Agent 등)
 * @returns {Promise<Object>} 업데이트된 공유 정보
 */
export async function useSharedPassword(accessToken, accessorId = null, accessorInfo = {}) {
  try {
    const shareRef = doc(db, 'shared_keys', accessToken);
    const shareDoc = await getDoc(shareRef);
    
    if (!shareDoc.exists()) {
      throw new Error('유효하지 않은 공유 링크입니다.');
    }
    
    const shareData = shareDoc.data();
    
    // 활성 상태 확인
    if (!shareData.isActive) {
      throw new Error('비활성화된 공유 링크입니다.');
    }
    
    // 만료 시간 확인
    if (new Date() > shareData.expiresAt.toDate()) {
      // 만료된 링크는 자동으로 비활성화
      await updateDoc(shareRef, { isActive: false });
      throw new Error('만료된 공유 링크입니다.');
    }
    
    // 사용 횟수 확인
    if (shareData.currentUses >= shareData.maxUses) {
      // 사용 한도 초과 시 자동으로 비활성화
      await updateDoc(shareRef, { isActive: false });
      throw new Error('사용 한도를 초과한 공유 링크입니다.');
    }
    
    // 🔥 사용 횟수 차감 및 접근 로그 기록
    const newCurrentUses = shareData.currentUses + 1;
    const accessLog = shareData.accessLog || [];
    
    // 접근 로그 추가
    const accessEntry = {
      accessedAt: new Date(),
      accessorId: accessorId || 'anonymous',
      accessorInfo: {
        userAgent: accessorInfo.userAgent || '',
        ip: accessorInfo.ip || '',
        ...accessorInfo
      }
    };
    
    accessLog.push(accessEntry);
    
    // 업데이트할 데이터 준비
    const updateData = {
      currentUses: newCurrentUses,
      accessLog,
      lastAccessedAt: new Date()
    };
    
    // 사용 한도에 도달한 경우 비활성화
    if (newCurrentUses >= shareData.maxUses) {
      updateData.isActive = false;
      updateData.completedAt = new Date();
      if (import.meta.env.DEV) {
        console.log('🔒 공유키 사용 한도 도달로 자동 비활성화');
      }
    }
    
    // Firestore 업데이트
    await updateDoc(shareRef, updateData);
    
    // 패스워드 데이터 복호화
    // 새 방식: shareData.shareKey 사용
    // 구 방식: accessToken이 곧 shareKey (하위 호환성)
    const decryptKey = shareData.shareKey || accessToken;
    const decryptedData = await decrypt(shareData.encryptedData, decryptKey);
    const passwordData = JSON.parse(decryptedData);
    
    if (import.meta.env.DEV) {
      console.log(`✅ 공유키 사용 완료: ${newCurrentUses}/${shareData.maxUses}`);
    }
    
    // 메타데이터와 함께 반환 (🚨 shareNote는 포함하지 않음)
    return {
      passwordData,
      shareInfo: {
        sharedBy: shareData.sharedBy,
        sharedByName: shareData.sharedByName || '알 수 없음',
        sharedByEmail: shareData.sharedByEmail || '',
        createdAt: shareData.createdAt,
        expiresAt: shareData.expiresAt,
        remainingUses: shareData.maxUses - newCurrentUses,
        currentUses: newCurrentUses,
        isActive: updateData.isActive !== false
      }
    };
    
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('공유 패스워드 사용 실패:', error);
    }
    throw error;
  }
}

/**
 * 공유키 취소/비활성화
 * @param {string} accessToken - 취소할 액세스 토큰
 * @param {string} userId - 취소 요청 사용자 ID
 * @returns {Promise<boolean>} 취소 성공 여부
 */
export async function revokeShareKey(accessToken, userId) {
  try {
    const shareRef = doc(db, 'shared_keys', accessToken);
    const shareDoc = await getDoc(shareRef);
    
    if (!shareDoc.exists()) {
      throw new Error('공유키를 찾을 수 없습니다.');
    }
    
    const shareData = shareDoc.data();
    
    // 권한 확인 (공유한 사용자만 취소 가능)
    if (shareData.sharedBy !== userId) {
      throw new Error('공유키 취소 권한이 없습니다.');
    }
    
    // 비활성화
    await updateDoc(shareRef, { 
      isActive: false,
      revokedAt: new Date(),
      revokedBy: userId
    });
    
    if (import.meta.env.DEV) {
      console.log('공유키 취소 완료');
    }
    return true;
    
  } catch (error) {
    console.error('공유키 취소 실패:', error);
    console.error('오류 메시지:', error.message);
    console.error('오류 코드:', error.code);
    console.error('전체 오류 객체:', error);
    throw error;
  }
}

/**
 * 사용자의 활성 공유키 목록 조회 (메모 포함)
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} 활성 공유키 목록
 */
export async function getUserActiveShares(userId) {
  try {
    const sharesQuery = query(
      collection(db, 'shared_keys'),
      where('sharedBy', '==', userId),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(sharesQuery);
    const activeShares = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const now = new Date();
      const expiresAt = data.expiresAt.toDate();
      
      // 만료되지 않은 공유키만 포함
      if (expiresAt > now) {
        // 🔍 디버깅: 개별 공유 데이터 확인
        console.log('공유 데이터:', {
          shareKey: data.shareKey,
          passwordTitle: data.passwordTitle,
          title: data.title,
          passwordWebsite: data.passwordWebsite,
          shareNote: data.shareNote // 🆕 메모 디버깅
        });
        
        activeShares.push({
          shareKey: data.shareKey, // 실제 암호화 키 (내부용)
          accessToken: data.accessToken || doc.id, // URL에 사용될 토큰
          passwordTitle: data.passwordTitle || data.title || '제목 없음', // title 필드도 확인
          passwordWebsite: data.passwordWebsite || '',
          shareNote: data.shareNote || '', // 🆕 공유 메모 포함
          createdAt: data.createdAt,
          expiresAt: data.expiresAt,
          maxUses: data.maxUses,
          currentUses: data.currentUses,
          remainingUses: data.maxUses - data.currentUses,
          securityMetadata: data.securityMetadata
        });
      }
    });
    
    // 🔍 디버깅: 최종 결과 확인
    console.log('활성 공유키 목록:', activeShares);
    
    return activeShares;
    
  } catch (error) {
    console.error('활성 공유키 조회 실패:', error);
    throw error;
  }
}

/**
 * 만료된 공유키 정리 (복합 인덱스 사용)
 * @returns {Promise<number>} 정리된 공유키 수
 */
export async function cleanupExpiredShares() {
  try {
    const now = new Date();
    
    // 복합 쿼리: 복합 인덱스 사용으로 더 효율적
    const expiredQuery = query(
      collection(db, 'shared_keys'),
      where('isActive', '==', true),
      where('expiresAt', '<', now)
    );
    
    const querySnapshot = await getDocs(expiredQuery);
    let cleanedCount = 0;
    
    const updatePromises = [];
    querySnapshot.forEach((doc) => {
      updatePromises.push(
        updateDoc(doc.ref, { isActive: false })
      );
      cleanedCount++;
    });
    
    await Promise.all(updatePromises);
    
    console.log('✅ 만료된 공유키 정리 완료 (복합 인덱스 사용):', cleanedCount);
    return cleanedCount;
    
  } catch (error) {
    console.error('❌ 만료된 공유키 정리 실패:', error);
    // 에러가 발생해도 공유 목록 로드는 계속 진행
    return 0;
  }
}

/**
 * 암호학적으로 안전한 공유키 생성
 * @returns {string} 공유키 문자열
 */
function generateShareKey() {
  // Web Crypto API를 사용하여 안전한 랜덤 바이트 생성
  const randomBytes = new Uint8Array(32); // 256비트
  crypto.getRandomValues(randomBytes);
  
  // Base64 URL-safe 인코딩
  const base64String = btoa(String.fromCharCode(...randomBytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  
  // 공유키 형식: pk_{랜덤문자열}
  // pk = password key의 약자
  return `pk_${base64String}`;
}

/**
 * 공유 액세스 토큰 생성 (URL에 사용)
 * @returns {string} 액세스 토큰
 */
function generateAccessToken() {
  // 16바이트 (128비트) 랜덤 토큰
  const randomBytes = new Uint8Array(16);
  crypto.getRandomValues(randomBytes);
  
  // URL-safe 인코딩
  const token = btoa(String.fromCharCode(...randomBytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  
  return token;
}

/**
 * 공유 URL 생성
 * @param {string} accessToken - 액세스 토큰
 * @returns {string} 공유 URL
 */
export function generateShareURL(accessToken) {
  // 환경에 따라 다른 베이스 URL 사용
  let baseURL;
  
  if (import.meta.env.PROD) {
    // 프로덕션 환경: pw-wallet.com 도메인 사용
    baseURL = import.meta.env.VITE_APP_SHARE_BASE_URL || import.meta.env.VITE_APP_BASE_URL || 'https://pw-wallet.com';
  } else {
    // 개발 환경: 현재 origin 사용
    baseURL = window.location.origin;
  }
  
  return `${baseURL}/shared/${accessToken}`;
}

/**
 * 공유키 유효성 검증
 * @param {string} shareKey - 검증할 공유키
 * @returns {boolean} 유효성 여부
 */
export function validateShareKey(shareKey) {
  return shareKey && shareKey.startsWith('pk_') && shareKey.length > 40;
}

/**
 * 액세스 토큰 유효성 검증
 * @param {string} accessToken - 검증할 액세스 토큰
 * @returns {boolean} 유효성 여부
 */
export function validateAccessToken(accessToken) {
  return accessToken && accessToken.length >= 20;
}

/**
 * 공유 통계 계산
 * @param {Array} shares - 공유 목록
 * @returns {Object} 공유 통계
 */
export function calculateShareStats(shares) {
  const stats = {
    totalShares: shares.length,
    activeShares: 0,
    expiredShares: 0,
    completedShares: 0,
    totalUsage: 0,
    averageUsage: 0
  };

  const now = new Date();

  shares.forEach(share => {
    const expiresAt = share.expiresAt.toDate ? share.expiresAt.toDate() : new Date(share.expiresAt);
    const isExpired = now > expiresAt;
    const isCompleted = share.currentUses >= share.maxUses;

    if (isExpired) {
      stats.expiredShares++;
    } else if (isCompleted) {
      stats.completedShares++;
    } else {
      stats.activeShares++;
    }

    stats.totalUsage += share.currentUses;
  });

  stats.averageUsage = stats.totalShares > 0 ? (stats.totalUsage / stats.totalShares) : 0;

  return stats;
}

/**
 * 남은 시간 계산 및 포맷
 * @param {Date} expiresAt - 만료 시간
 * @returns {Object} 시간 정보
 */
export function calculateTimeRemaining(expiresAt) {
  const now = new Date();
  const expiry = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
  const diff = expiry - now;

  if (diff <= 0) {
    return {
      isExpired: true,
      text: '만료됨',
      urgency: 'expired'
    };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  let text;
  let urgency = 'normal';

  if (days > 0) {
    text = `${days}일 ${hours}시간 후`;
  } else if (hours > 0) {
    text = `${hours}시간 ${minutes}분 후`;
    if (hours < 3) urgency = 'urgent';
  } else {
    text = `${minutes}분 후`;
    urgency = 'critical';
  }

  return {
    isExpired: false,
    text,
    urgency,
    days,
    hours,
    minutes
  };
}